import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import { api, formatError } from "../lib/api";
import { BarChart3, ArrowUp, ArrowDown, Loader2 } from "lucide-react";
import { fmtDate } from "../lib/progressFormat";

const COLUMNS = [
  { key: "name", label: "User", text: true },
  { key: "package_slug", label: "Plan", text: true },
  { key: "percent_complete", label: "% Complete" },
  { key: "videos_watched", label: "Videos Watched" },
  { key: "readings_reviewed", label: "Readings Reviewed" },
  { key: "quizzes_passed", label: "Quizzes Passed" },
  { key: "worksheets_submitted", label: "Worksheets" },
  { key: "final_exam_passed", label: "Final Exam" },
  { key: "certificates", label: "Certificates" },
  { key: "last_progress_at", label: "Last Activity", text: true },
];

function sortRows(rows, key, dir) {
  const val = (r) => (key === "name" ? (r.name || r.email || "") : r[key]);
  return [...rows].sort((a, b) => {
    const x = val(a) ?? "", y = val(b) ?? "";
    const c = typeof x === "string" ? String(x).localeCompare(String(y)) : Number(x) - Number(y);
    return dir === "asc" ? c : -c;
  });
}

function SortHeader({ col, sort, onSort }) {
  const active = sort.key === col.key;
  const Icon = sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className="px-3 py-2.5 text-left whitespace-nowrap">
      <button type="button" onClick={() => onSort(col)}
        className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-semibold ${active ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}
        data-testid={`progress-sort-${col.key}`}>
        {col.label}{active && <Icon className="w-3 h-3" />}
      </button>
    </th>
  );
}

function ProgressBar({ pct }) {
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden">
        <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-semibold tabular-nums w-11 text-right">{pct}%</span>
    </div>
  );
}

function UserRow({ r }) {
  return (
    <tr className="border-t border-border hover:bg-muted/30" data-testid={`progress-row-${r.user_id}`}>
      <td className="px-3 py-2.5">
        <Link to={`/admin/progress/${r.user_id}`} className="font-semibold text-foreground hover:text-primary"
          data-testid={`progress-open-${r.user_id}`}>{r.name || r.email}</Link>
        <div className="text-xs text-muted-foreground">{r.email}</div>
      </td>
      <td className="px-3 py-2.5 text-xs capitalize">{r.package_slug || "—"}</td>
      <td className="px-3 py-2.5"><ProgressBar pct={r.percent_complete} /></td>
      <td className="px-3 py-2.5 tabular-nums">{r.videos_watched}/{r.modules_total}</td>
      <td className="px-3 py-2.5 tabular-nums">{r.readings_reviewed}/{r.modules_total}</td>
      <td className="px-3 py-2.5 tabular-nums">{r.quizzes_passed}/{r.modules_total}</td>
      <td className="px-3 py-2.5 tabular-nums">{r.worksheets_submitted}/{r.worksheets_total}</td>
      <td className="px-3 py-2.5">{r.final_exam_passed
        ? <span className="text-emerald-700 font-semibold text-xs">Passed</span>
        : <span className="text-muted-foreground text-xs">—</span>}</td>
      <td className="px-3 py-2.5 tabular-nums">{r.certificates}</td>
      <td className="px-3 py-2.5 text-xs whitespace-nowrap">{fmtDate(r.last_progress_at)}</td>
    </tr>
  );
}

export default function AdminLearnerProgress() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [sort, setSort] = useState({ key: "percent_complete", dir: "desc" });

  useEffect(() => {
    api.get("/admin/progress/users").then((r) => setRows(r.data.users)).catch((e) => setError(formatError(e)));
  }, []);

  const sorted = useMemo(() => (rows ? sortRows(rows, sort.key, sort.dir) : []), [rows, sort]);
  const onSort = (col) => setSort((s) => (s.key === col.key
    ? { key: col.key, dir: s.dir === "asc" ? "desc" : "asc" }
    : { key: col.key, dir: col.text ? "asc" : "desc" }));

  return (
    <AppShell>
      <PageHero eyebrow="Admin" icon={BarChart3}
        title={<>Learner <span className="text-grad">Progress</span></>}
        subtitle="What every user has watched, reviewed and completed. Select a user for the full module breakdown."
        testid="learner-progress-hero" />
      {error && <p className="text-sm text-destructive" data-testid="learner-progress-error">{error}</p>}
      {!rows && !error && <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" data-testid="learner-progress-loading" />}
      {rows && (
        <div className="rounded-2xl border border-border bg-white overflow-x-auto" data-testid="learner-progress-table">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr>{COLUMNS.map((c) => <SortHeader key={c.key} col={c} sort={sort} onSort={onSort} />)}</tr>
            </thead>
            <tbody>{sorted.map((r) => <UserRow key={r.user_id} r={r} />)}</tbody>
          </table>
        </div>
      )}
      {rows && (
        <p className="mt-3 text-xs text-muted-foreground" data-testid="learner-progress-footnote">
          {rows.length} users · % Complete counts each module's video, reading and quiz, every workbook worksheet, and the final exam.
        </p>
      )}
    </AppShell>
  );
}
